/**
 * EvalResultDetailDialog Component
 * Modal dialog showing detailed evaluation result including LLM judgments
 */

import { useEffect } from 'react';
import { X, Clock, Calendar, User, Cpu, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { EvalResultDetail, MetricResult } from '@/types/evalScenarios';

interface EvalResultDetailDialogProps {
  result: EvalResultDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

export function EvalResultDetailDialog({
  result,
  isOpen,
  onClose,
}: EvalResultDetailDialogProps) {
  const { messages, language } = useI18n();
  const t = messages.evalScenarios;
  const tc = messages.common;

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !result) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const score = result.overall_score != null ? (result.overall_score * 100).toFixed(1) : null;
  const isRunning = result.status === 'running';
  const isFailed = result.status === 'failed';

  const renderMetricCard = (metric: MetricResult) => {
    const scorePercent = (metric.score * 100).toFixed(1);
    const thresholdPercent = (metric.threshold * 100).toFixed(0);

    return (
      <div
        key={metric.name}
        className={`border rounded-lg p-4 ${
          metric.passed
            ? 'border-green-200 bg-green-50'
            : 'border-red-200 bg-red-50'
        }`}
      >
        {/* Metric Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {metric.passed ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            <span className="font-medium text-gray-900 capitalize">
              {metric.name.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="text-right">
            <span
              className={`text-lg font-bold ${
                metric.passed ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {scorePercent}%
            </span>
            <span className="text-sm text-gray-500 ml-1">
              / {thresholdPercent}%
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
          {/* Threshold marker */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10"
            style={{ left: `${metric.threshold * 100}%` }}
          />
          {/* Score bar */}
          <div
            className={`h-full transition-all ${
              metric.passed ? 'bg-green-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(metric.score * 100, 100)}%` }}
          />
        </div>

        {/* LLM Judgment Reason */}
        {metric.reason && (
          <div className="mt-2">
            <div className="text-xs font-medium text-gray-500 mb-1">
              {t.resultDetail?.judgmentReason || 'LLM 判断理由'}
            </div>
            <div className="text-sm text-gray-700 bg-white/50 rounded p-2 whitespace-pre-wrap">
              {metric.reason}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-4 md:inset-8 lg:inset-16 bg-white rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {isRunning ? '🔄' : isFailed ? '💥' : result.passed ? '✅' : '❌'}
            </span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t.resultDetail?.title || '评测结果详情'}
              </h2>
              <p className="text-sm text-gray-500">
                {result.scenario_name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status Banner for Running/Failed */}
          {isRunning && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
              <span className="text-2xl animate-spin">🔄</span>
              <div>
                <div className="font-medium text-blue-800">
                  {t.historyPanel?.running || '运行中...'}
                </div>
                <div className="text-sm text-blue-600">
                  {t.resultDetail?.runningHint || '评测正在进行中，请稍后刷新查看结果'}
                </div>
              </div>
            </div>
          )}

          {isFailed && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">💥</span>
                <div className="font-medium text-red-800">
                  {t.historyPanel?.failed || '执行失败'}
                </div>
              </div>
              {result.error && (
                <div className="text-sm text-red-700 bg-red-100 rounded p-2 mt-2">
                  {result.error}
                </div>
              )}
            </div>
          )}

          {/* Summary Card */}
          {!isRunning && !isFailed && (
            <div
              className={`rounded-lg p-6 ${
                result.passed
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    {t.resultDetail?.overallScore || '综合得分'}
                  </div>
                  <div
                    className={`text-4xl font-bold ${
                      result.passed ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {score}%
                  </div>
                </div>
                <div
                  className={`px-4 py-2 rounded-full text-lg font-medium ${
                    result.passed
                      ? 'bg-green-200 text-green-800'
                      : 'bg-red-200 text-red-800'
                  }`}
                >
                  {result.passed ? (t.passed || '通过') : (t.failed || '失败')}
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs">{t.resultDetail?.executedAt || '执行时间'}</span>
              </div>
              <div className="text-sm font-medium text-gray-900">
                {formatDate(result.executed_at)}
              </div>
            </div>

            {result.model_id && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Cpu className="w-4 h-4" />
                  <span className="text-xs">{t.resultDetail?.model || '模型'}</span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {result.model_id}
                </div>
              </div>
            )}

            {result.response_time_ms && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs">{t.resultDetail?.responseTime || '响应时间'}</span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {(result.response_time_ms / 1000).toFixed(2)}s
                </div>
              </div>
            )}

            {result.executed_by && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <User className="w-4 h-4" />
                  <span className="text-xs">{t.resultDetail?.executedBy || '执行者'}</span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {result.executed_by}
                </div>
              </div>
            )}
          </div>

          {/* Metrics */}
          {result.metrics && result.metrics.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t.resultDetail?.metricsTitle || '评测指标详情'}
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                {result.metrics.map(renderMetricCard)}
              </div>
            </div>
          )}

          {/* Agent Response */}
          {result.response && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                {t.historyPanel?.agentResponse || 'Agent 响应'}
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                  {result.response}
                </pre>
              </div>
            </div>
          )}

          {/* Run ID */}
          {result.run_id && (
            <div className="text-xs text-gray-400 text-right">
              Run ID: {result.run_id}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button variant="secondary" onClick={onClose}>
            {tc.close}
          </Button>
        </div>
      </div>
    </>
  );
}
